import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { motion } from "framer-motion";
import type { MotionProps } from "framer-motion";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import SellRoundedIcon from "@mui/icons-material/SellRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import CurrencyBitcoinRoundedIcon from "@mui/icons-material/CurrencyBitcoinRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import { useNavigate } from "react-router-dom";
import { SkinPickerCarousel } from "../../pages/Whitelabel/SkinPickerCarousel";
import { WhitelabelPackagePicker } from "../../pages/Whitelabel/WhitelabelPackagePicker";
import {
  GUIDE_WL_BRAND_NAME,
  GUIDE_WL_CATEGORIES,
  GUIDE_WL_DOMAIN_SUFFIX,
  GUIDE_WL_PACKAGES,
  GUIDE_WL_SKINS,
  GUIDE_WL_SUBDOMAIN,
  GUIDE_WL_THEME_CONFIGS,
  type GuideWlThemeCategory,
} from "./fixtures";

const BTN_PRIMARY =
  "font-semibold shadow-[0_2px_12px_color-mix(in_srgb,var(--border-color)_25%,transparent)] transition-[opacity,transform] hover:opacity-95 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent disabled:opacity-45 disabled:cursor-not-allowed";
const BTN_PRIMARY_CLASS = `p-2.5 text-sm text-white rounded-lg border-2 border-[color-mix(in_srgb,var(--border-color)_50%,transparent)] bg-[color-mix(in_srgb,var(--border-color)_28%,#0c1222)] ${BTN_PRIMARY}`;
const BTN_SECONDARY_CLASS =
  "p-2.5 text-sm text-zinc-300 rounded-lg border border-zinc-600/90 bg-zinc-800/60 transition-colors hover:bg-zinc-700/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-500 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent";

const STEPPER_PRIMARY = "var(--border-color)";
const STEPPER_LINE_GRAY = "var(--wl-stepper-line-gray)";
const STEPPER_INACTIVE_FILL = "var(--wl-stepper-inactive-fill)";
const STEPPER_INACTIVE_BORDER = "var(--wl-stepper-inactive-border)";

const STEPPER_STEPS = [
  { key: "branding", label: "Brand" },
  { key: "setup", label: "Basics" },
  { key: "packages", label: "Packages" },
  { key: "review", label: "Confirm" },
] as const;

const PULSE_EASE = [0.42, 0, 0.58, 1] as const;

function pulseMotion(pulseId: string | null, controlId: string): Pick<MotionProps, "animate" | "transition"> {
  const on = pulseId === controlId;
  if (on) {
    return {
      animate: {
        scale: [1, 0.93, 1],
        boxShadow: [
          "0 0 0 0px transparent",
          "0 0 0 3px color-mix(in srgb, var(--border-color) 48%, transparent)",
          "0 0 0 0px transparent",
        ],
      },
      transition: { duration: 0.34, ease: PULSE_EASE },
    };
  }
  return {
    animate: { scale: 1 },
    transition: { duration: 0.12, ease: PULSE_EASE },
  };
}

function hexToRgbParts(hex: string): [number, number, number] {
  const raw = hex.replace("#", "").trim();
  if (raw.length === 3) {
    const r = parseInt(raw[0] + raw[0], 16);
    const g = parseInt(raw[1] + raw[1], 16);
    const b = parseInt(raw[2] + raw[2], 16);
    return [r, g, b];
  }
  const n = parseInt(raw.slice(0, 6), 16);
  if (Number.isNaN(n)) return [12, 18, 32];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

const formatPackagePoints = (n: number) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);

const formatPackagePrice = (amount: number, currency = "INR") => {
  try {
    const locale = currency === "INR" ? "en-IN" : undefined;
    const safeAmount = Number.isFinite(amount) ? amount : 0;
    const roundedAmount = Math.round(safeAmount);
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(roundedAmount);
  } catch {
    const safeAmount = Number.isFinite(amount) ? amount : 0;
    return `${Math.round(safeAmount)} ${currency}`;
  }
};

export type CreateWhitelabelGuideShellProps = {
  flowStepIndex: number;
  onFlowStepChange: (next: number) => void;
  onRequestBack: () => void;
  /** When true, shell does not receive pointer events (Help autopilot). */
  blockPointerForAutopilot?: boolean;
};

export type CreateWhitelabelGuideShellRef = {
  resetDemoFields: () => void;
  typeBrand: (target: string) => Promise<void>;
  typeSubdomain: (target: string) => Promise<void>;
  pulseControl: (selector: string) => Promise<void>;
  setSkinId: (skinId: string) => void;
  selectTheme: (category: string, themeName: string) => void;
  ensureCategory: (key: string, selected: boolean) => void;
  setPackageId: (packageId: string) => void;
};

const typewriterDelayMs = 58;

const delay = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

export const CreateWhitelabelGuideShell = forwardRef<
  CreateWhitelabelGuideShellRef,
  CreateWhitelabelGuideShellProps
>(function CreateWhitelabelGuideShell(
  { flowStepIndex, onFlowStepChange, onRequestBack, blockPointerForAutopilot = false },
  ref
) {
  const navigate = useNavigate();
  const stepperVisualIdx = Math.min(flowStepIndex, 3);
  const stepperLinesDoneThrough = stepperVisualIdx;

  const [brandName, setBrandName] = useState(GUIDE_WL_BRAND_NAME);
  const [subdomain, setSubdomain] = useState(GUIDE_WL_SUBDOMAIN);
  const [selectedSkinId, setSelectedSkinId] = useState(GUIDE_WL_SKINS[0]?.skinId ?? "");
  const [selectedCategories, setSelectedCategories] = useState<string[]>(["sports"]);
  const [activeThemeBrowseCategory, setActiveThemeBrowseCategory] = useState(
    GUIDE_WL_THEME_CONFIGS[0]?.category ?? ""
  );
  const [selectedThemeCategory, setSelectedThemeCategory] = useState(
    GUIDE_WL_THEME_CONFIGS[0]?.category ?? ""
  );
  const [selectedThemeName, setSelectedThemeName] = useState(
    GUIDE_WL_THEME_CONFIGS[0]?.themes[0]?.name ?? ""
  );
  const [enableCrypto, setEnableCrypto] = useState(true);
  const [enableFiat, setEnableFiat] = useState(true);
  const [fiatBankSource, setFiatBankSource] = useState<"own" | "provided">("own");
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(
    GUIDE_WL_PACKAGES[0]?.packageId ?? null
  );
  const [pulseId, setPulseId] = useState<string | null>(null);

  const pulseControl = useCallback(async (selector: string) => {
    const id = selector.startsWith("#") ? selector.slice(1) : selector;
    setPulseId(id);
    await delay(420);
    setPulseId(null);
    await delay(120);
  }, []);

  const typeBrand = useCallback(async (target: string) => {
    setBrandName("");
    for (let i = 1; i <= target.length; i += 1) {
      setBrandName(target.slice(0, i));
      await delay(typewriterDelayMs);
    }
  }, []);

  const typeSubdomain = useCallback(async (target: string) => {
    const t = target.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
    setSubdomain("");
    for (let i = 1; i <= t.length; i += 1) {
      setSubdomain(t.slice(0, i));
      await delay(typewriterDelayMs);
    }
  }, []);

  const resetDemoFields = useCallback(() => {
    setBrandName("");
    setSubdomain("");
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      resetDemoFields,
      typeBrand,
      typeSubdomain,
      pulseControl,
      setSkinId: setSelectedSkinId,
      selectTheme: (category: string, themeName: string) => {
        setActiveThemeBrowseCategory(category);
        setSelectedThemeCategory(category);
        setSelectedThemeName(themeName);
      },
      ensureCategory: (key: string, selected: boolean) => {
        setSelectedCategories((prev) => {
          if (selected) return prev.includes(key) ? prev : [...prev, key];
          return prev.filter((k) => k !== key);
        });
      },
      setPackageId: setSelectedPackageId,
    }),
    [onFlowStepChange, pulseControl, resetDemoFields, typeBrand, typeSubdomain]
  );

  useEffect(() => {
    if (flowStepIndex === 2 && GUIDE_WL_PACKAGES.length && !selectedPackageId) {
      setSelectedPackageId(GUIDE_WL_PACKAGES[0].packageId);
    }
  }, [flowStepIndex, selectedPackageId]);

  const activeThemeCategory: GuideWlThemeCategory | undefined = useMemo(
    () => GUIDE_WL_THEME_CONFIGS.find((c) => c.category === activeThemeBrowseCategory),
    [activeThemeBrowseCategory]
  );

  const selectThemeCard = useCallback((category: string, themeName: string) => {
    setSelectedThemeCategory(category);
    setSelectedThemeName(themeName);
  }, []);

  const toggleCategory = useCallback((key: string) => {
    setSelectedCategories((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }, []);

  const skinLabel = useMemo(() => {
    const s = GUIDE_WL_SKINS.find((x) => x.skinId === selectedSkinId);
    return s?.name ?? "—";
  }, [selectedSkinId]);

  const themeSummary = useMemo(() => {
    if (!selectedThemeCategory || !selectedThemeName) return "—";
    return `${selectedThemeCategory} — ${selectedThemeName}`;
  }, [selectedThemeCategory, selectedThemeName]);

  const productsSummary = useMemo(() => {
    if (!selectedCategories.length) return "—";
    return selectedCategories
      .map((k) => GUIDE_WL_CATEGORIES.find((c) => c.key === k)?.label || k)
      .join(", ");
  }, [selectedCategories]);

  const paymentsSummary = useMemo(() => {
    const p: string[] = [];
    if (enableCrypto) p.push("Crypto");
    if (enableFiat) p.push("Fiat");
    return p.length ? p.join(", ") : "—";
  }, [enableCrypto, enableFiat]);

  const planSummary = useMemo(() => {
    const p = GUIDE_WL_PACKAGES.find((x) => x.packageId === selectedPackageId);
    if (!p) return "—";
    const name = p.name?.trim() || "Plan";
    return `${name} — ${formatPackagePoints(p.pointsCredited)} pts — ${formatPackagePrice(
      p.payAmount,
      p.currency || "INR"
    )}`;
  }, [selectedPackageId]);

  const webAddressSummary = useMemo(
    () => `https://${subdomain.trim().toLowerCase() || "yoursite"}.${GUIDE_WL_DOMAIN_SUFFIX}`,
    [subdomain]
  );

  const handleNext = () => {
    onFlowStepChange(Math.min(flowStepIndex + 1, STEPPER_STEPS.length - 1));
  };

  const handlePrev = () => {
    if (flowStepIndex <= 0) return;
    onRequestBack();
  };

  const wlStepperIcon = (idx: number) => {
    const sx = { fontSize: 22, color: "#FFFFFF" };
    switch (STEPPER_STEPS[idx]?.key) {
      case "setup":
        return <LockRoundedIcon sx={sx} />;
      case "branding":
        return <PersonRoundedIcon sx={sx} />;
      case "packages":
        return <AccountBalanceWalletRoundedIcon sx={sx} />;
      case "review":
        return <CheckRoundedIcon sx={sx} />;
      default:
        return <LockRoundedIcon sx={sx} />;
    }
  };

  return (
    <div className="text-white box-border mt-[3.75rem] flex min-h-0 flex-col overflow-x-visible overflow-y-hidden px-3 pb-[100px] pt-2 sm:px-4 sm:pt-3">
      <div
        className={`mx-auto flex w-full max-w-4xl flex-1 flex-col${
          blockPointerForAutopilot ? " pointer-events-none" : ""
        }`}
      >
        <header className="flex min-w-0 shrink-0 items-center gap-0 pb-1.5">
          <button
            type="button"
            onClick={() => navigate("/whitelabel")}
            className="group inline-flex shrink-0 items-center justify-center rounded-none bg-transparent py-2 pl-1 pr-0.5 text-zinc-100 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Back to whitelabels"
          >
            <ArrowBackIcon sx={{ fontSize: 22, color: "currentColor" }} />
          </button>
          <h1
            className="m-0 min-w-0 flex-1 text-lg font-semibold uppercase leading-tight tracking-wide sm:text-xl"
            style={{ color: STEPPER_PRIMARY }}
          >
            Create your brand (tour)
          </h1>
        </header>

        <div className="shrink-0 py-1 sm:py-1.5">
          <div className="flex w-full flex-row items-start" data-wl-guide-stepper="true">
            {STEPPER_STEPS.map((s, idx) => (
              <div key={s.key} className="flex min-w-0 flex-1 flex-col items-center">
                <div className="flex w-full items-center">
                  {idx === 0 ? (
                    <div className="min-h-1 flex-1" aria-hidden />
                  ) : (
                    <div
                      className="h-1 min-h-1 flex-1 rounded-l-full transition-[background-color] duration-200 ease-out"
                      style={{
                        backgroundColor:
                          stepperLinesDoneThrough >= idx ? STEPPER_PRIMARY : STEPPER_LINE_GRAY,
                      }}
                    />
                  )}
                  <div
                    className="relative z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 transition-[box-shadow,background-color,border-color] duration-200 ease-out"
                    style={
                      stepperVisualIdx === idx
                        ? {
                            backgroundColor: STEPPER_PRIMARY,
                            borderColor: STEPPER_PRIMARY,
                            boxShadow:
                              "0 4px 16px color-mix(in srgb, var(--border-color) 42%, transparent)",
                          }
                        : {
                            backgroundColor: STEPPER_INACTIVE_FILL,
                            borderColor: STEPPER_INACTIVE_BORDER,
                          }
                    }
                  >
                    {wlStepperIcon(idx)}
                  </div>
                  {idx === STEPPER_STEPS.length - 1 ? (
                    <div className="min-h-1 flex-1" aria-hidden />
                  ) : (
                    <div
                      className="h-1 min-h-1 flex-1 rounded-r-full transition-[background-color] duration-200 ease-out"
                      style={{
                        backgroundColor:
                          stepperLinesDoneThrough > idx ? STEPPER_PRIMARY : STEPPER_LINE_GRAY,
                      }}
                    />
                  )}
                </div>
                <p
                  className={`mt-2 w-full px-0.5 text-center text-[11px] leading-tight sm:text-sm ${
                    stepperVisualIdx === idx ? "font-bold" : "font-medium text-zinc-400"
                  }`}
                  style={stepperVisualIdx === idx ? { color: STEPPER_PRIMARY } : undefined}
                >
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain pb-8 [-webkit-overflow-scrolling:touch]">
          <div className="overflow-visible rounded-xl border border-border-color bg-inputbox-color">
            <>
            {flowStepIndex === 0 && (
              <div className="p-3">
                <div>
                  <label className="inline-flex items-center gap-2 text-base font-semibold text-zinc-200">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-orange-500/90">
                      <SellRoundedIcon sx={{ fontSize: 13, color: "#fff" }} />
                    </span>
                    <span>
                      Brand name <span className="text-red-500">*</span>
                    </span>
                  </label>
                </div>
                <input
                  id="wl-tour-brand-name"
                  className="mt-1 w-full rounded-lg border border-border-color bg-inputbox-color p-2.5 text-sm text-white"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="e.g. BetZ, WinSpin"
                />

                <div className="mt-2">
                  <label className="inline-flex items-center gap-2 text-base font-semibold text-zinc-200">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-emerald-500/90">
                      <PublicRoundedIcon sx={{ fontSize: 13, color: "#fff" }} />
                    </span>
                    <span>
                      Select your web address <span className="text-red-500">*</span>
                    </span>
                  </label>
                  <div
                    id="wl-tour-subdomain-row"
                    className="mt-1.5 flex min-w-0 flex-nowrap items-center gap-y-1 overflow-x-auto rounded-lg border border-border-color bg-inputbox-color"
                  >
                    <span className="select-none pl-2.5 font-mono text-xs text-zinc-500 sm:pl-3 sm:text-sm">
                      https://
                    </span>
                    <input
                      className="min-w-[6rem] flex-1 border-0 bg-transparent py-2.5 pl-1 pr-1 text-sm text-white outline-none"
                      value={subdomain}
                      onChange={(e) => setSubdomain(e.target.value.replace(/[^a-zA-Z0-9]/g, "").toLowerCase())}
                      placeholder="yoursite"
                    />
                    <span className="select-none pr-2.5 font-mono text-xs text-zinc-500 sm:pr-3 sm:text-sm">
                      .{GUIDE_WL_DOMAIN_SUFFIX}
                    </span>
                  </div>
                </div>

                <div id="wl-tour-logo-upload" className="mt-3 rounded-lg border border-border-color/60 p-2">
                  <p className="text-sm font-semibold text-zinc-200">Logo</p>
                  <p className="mt-0.5 text-xs text-zinc-400">PNG upload (tour preview only)</p>
                  <label className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border-color bg-inputbox-color px-3 py-2 text-sm text-zinc-200">
                    <CloudUploadRoundedIcon sx={{ fontSize: 18 }} />
                    Choose file
                    <input type="file" accept="image/png" className="hidden" tabIndex={-1} />
                  </label>
                </div>

                <div className="mt-3 flex justify-end">
                  <motion.button
                    id="wl-tour-next-branding"
                    type="button"
                    className={`min-w-[120px] ${BTN_PRIMARY_CLASS}`}
                    onClick={handleNext}
                    {...pulseMotion(pulseId, "wl-tour-next-branding")}
                  >
                    Next
                  </motion.button>
                </div>
              </div>
            )}

            {flowStepIndex === 1 && (
              <div className="p-3">
                <div>
                  <p className="inline-flex items-center gap-2 text-base font-semibold text-zinc-200">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-fuchsia-500/90">
                      <PaletteRoundedIcon sx={{ fontSize: 13, color: "#fff" }} />
                    </span>
                    <span>
                      Look &amp; theme <span className="text-red-500">*</span>
                    </span>
                  </p>
                  <p className="mt-1 text-xs text-zinc-400">Pick a skin that matches your brand.</p>
                </div>

                <motion.div id="wl-tour-skin" {...pulseMotion(pulseId, "wl-tour-skin")}>
                  <SkinPickerCarousel
                    skins={GUIDE_WL_SKINS}
                    selectedSkinId={selectedSkinId}
                    onSelectSkin={setSelectedSkinId}
                  />
                </motion.div>

                <div id="wl-tour-theme" className="mt-3 border-t border-border-color/60 pt-3">
                  <p className="inline-flex items-center gap-2 text-base font-semibold text-zinc-200">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-violet-500/90 text-[11px] font-bold text-white">
                      T
                    </span>
                    <span>
                      Brand theme <span className="text-red-500">*</span>
                    </span>
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {GUIDE_WL_THEME_CONFIGS.map((cfg) => {
                      const active = cfg.category === activeThemeBrowseCategory;
                      return (
                        <button
                          key={cfg.category}
                          type="button"
                          onClick={() => setActiveThemeBrowseCategory(cfg.category)}
                          className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                            active
                              ? "border-[var(--border-color)] bg-[color-mix(in_srgb,var(--border-color)_28%,transparent)] text-white"
                              : "border-border-color bg-inputbox-color/30 text-zinc-300 hover:border-zinc-500"
                          }`}
                        >
                          {cfg.category}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {(activeThemeCategory?.themes ?? []).map((theme) => {
                      const isSelected =
                        selectedThemeCategory === activeThemeCategory?.category &&
                        selectedThemeName === theme.name;
                      const [pr, pg, pb] = hexToRgbParts(theme.primary);
                      const [sr, sg, sb] = hexToRgbParts(theme.secondary);
                      const [br, bg, bb] = hexToRgbParts(theme.background);
                      return (
                        <motion.button
                          key={`${activeThemeCategory?.category}-${theme.name}`}
                          id={`wl-tour-theme-${theme.name.replace(/\s+/g, "-")}`}
                          type="button"
                          onClick={() =>
                            selectThemeCard(activeThemeCategory!.category, theme.name)
                          }
                          className={`relative flex min-h-[3.15rem] w-full items-center justify-between overflow-hidden rounded-xl border px-0 py-0 text-left transition ${
                            isSelected
                              ? "border-[var(--border-color)] shadow-[0_0_0_1px_color-mix(in_srgb,var(--border-color)_55%,transparent),0_10px_24px_rgba(0,0,0,0.35)]"
                              : "border-border-color/60 hover:border-zinc-500"
                          }`}
                          style={{
                            background: `linear-gradient(108deg, rgba(${br},${bg},${bb},0.6) 0%, rgba(${pr},${pg},${pb},0.18) 54%, rgba(${sr},${sg},${sb},0.2) 100%)`,
                          }}
                          {...pulseMotion(
                            pulseId,
                            `wl-tour-theme-${theme.name.replace(/\s+/g, "-")}`
                          )}
                        >
                          <div className="absolute bottom-0 left-0 top-0 flex w-[8px] flex-col">
                            <div className="flex-1" style={{ backgroundColor: theme.primary }} />
                            <div className="flex-1" style={{ backgroundColor: theme.secondary }} />
                            <div className="flex-1" style={{ backgroundColor: theme.background }} />
                          </div>
                          <div className="flex min-w-0 flex-1 items-center pl-5 pr-2">
                            <p className="whitespace-normal break-words text-sm font-semibold leading-tight text-zinc-100">
                              {theme.name}
                            </p>
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>
                </div>

                <div id="wl-tour-products" className="mt-3 border-t border-border-color/60 pt-3">
                  <p className="inline-flex items-center gap-2 text-base font-semibold text-zinc-200">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-sky-500/90">
                      <Inventory2RoundedIcon sx={{ fontSize: 13, color: "#fff" }} />
                    </span>
                    <span>
                      Products <span className="text-red-500">*</span>
                    </span>
                  </p>
                  <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2">
                    {GUIDE_WL_CATEGORIES.map((cat) => {
                      const selected = selectedCategories.includes(cat.key);
                      return (
                        <motion.label
                          key={cat.key}
                          id={`wl-tour-cat-${cat.key}`}
                          className="flex min-w-0 cursor-pointer items-center gap-2.5 text-sm text-zinc-200"
                          {...pulseMotion(pulseId, `wl-tour-cat-${cat.key}`)}
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleCategory(cat.key)}
                            className="h-4.5 w-4.5 shrink-0 cursor-pointer accent-border-color"
                          />
                          <span className="min-w-0 leading-snug">{cat.label}</span>
                        </motion.label>
                      );
                    })}
                  </div>
                </div>

                <motion.div
                  id="wl-tour-payment-modes"
                  className="mt-3 border-t border-border-color/60 pt-3"
                  {...pulseMotion(pulseId, "wl-tour-payment-modes")}
                >
                  <div className="rounded-2xl border border-[color-mix(in_srgb,var(--border-color)_45%,#1f3444)] bg-[color-mix(in_srgb,var(--border-color)_9%,#081722)] p-3">
                    <div className="text-center">
                      <p className="inline-flex items-center gap-2 text-base font-semibold text-zinc-100">
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-amber-500/90">
                          <CreditCardRoundedIcon sx={{ fontSize: 13, color: "#fff" }} />
                        </span>
                        <span>
                          Payment Modes <span className="text-red-500">*</span>
                        </span>
                      </p>
                    </div>
                    <div className="mt-3 space-y-2" role="group" aria-label="Payment modes">
                      <label
                        className={`group flex cursor-pointer items-center gap-2.5 rounded-xl border px-2.5 py-2 ${
                          enableCrypto
                            ? "border-[color-mix(in_srgb,var(--border-color)_70%,#22d3ee)] bg-[color-mix(in_srgb,var(--border-color)_12%,#0b2230)]"
                            : "border-border-color/45 bg-inputbox-color/35"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={enableCrypto}
                          onChange={() => setEnableCrypto((v) => !v)}
                          className="h-4 w-4 shrink-0 cursor-pointer accent-border-color"
                        />
                        <span className="text-sm font-semibold text-zinc-100">Crypto</span>
                        <span className="ml-auto text-cyan-200" aria-hidden>
                          <CurrencyBitcoinRoundedIcon sx={{ fontSize: 16 }} />
                        </span>
                      </label>
                      <div
                        className={`rounded-xl border ${
                          enableFiat
                            ? "border-[color-mix(in_srgb,var(--border-color)_70%,#22d3ee)] bg-[color-mix(in_srgb,var(--border-color)_12%,#0b2230)]"
                            : "border-border-color/45 bg-inputbox-color/35"
                        }`}
                      >
                        <label className="group flex cursor-pointer items-center gap-2.5 px-2.5 py-2">
                          <input
                            type="checkbox"
                            checked={enableFiat}
                            onChange={() => setEnableFiat((v) => !v)}
                            className="h-4 w-4 shrink-0 cursor-pointer accent-border-color"
                          />
                          <span className="text-sm font-semibold text-zinc-100">Fiat</span>
                          <span className="ml-auto text-cyan-200" aria-hidden>
                            <AccountBalanceRoundedIcon sx={{ fontSize: 15 }} />
                          </span>
                        </label>
                        <div
                          className={`border-t px-3 pb-2 pt-2 ${
                            enableFiat ? "opacity-100" : "opacity-50"
                          }`}
                        >
                          <p className="text-xs font-medium text-zinc-300">Payout setup:</p>
                          <div className="mt-1.5 space-y-1.5">
                            <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-200">
                              <input
                                type="radio"
                                name="fiatBankSource-guide"
                                checked={fiatBankSource === "own"}
                                onChange={() => setFiatBankSource("own")}
                                disabled={!enableFiat}
                                className="accent-border-color"
                              />
                              <span>Use my own bank accounts</span>
                            </label>
                            <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-200">
                              <input
                                type="radio"
                                name="fiatBankSource-guide"
                                checked={fiatBankSource === "provided"}
                                onChange={() => setFiatBankSource("provided")}
                                disabled={!enableFiat}
                                className="accent-border-color"
                              />
                              <span>Use platform-managed bank accounts</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <div className="mt-3 flex gap-2">
                  <button type="button" className={`w-1/2 ${BTN_SECONDARY_CLASS}`} onClick={handlePrev}>
                    Back
                  </button>
                  <motion.button
                    id="wl-tour-next-setup"
                    type="button"
                    className={`w-1/2 ${BTN_PRIMARY_CLASS}`}
                    onClick={handleNext}
                    {...pulseMotion(pulseId, "wl-tour-next-setup")}
                  >
                    Next
                  </motion.button>
                </div>
              </div>
            )}

            {flowStepIndex === 2 && (
              <div className="p-3">
                <motion.div id="wl-tour-packages" {...pulseMotion(pulseId, "wl-tour-packages")}>
                  <div className="mt-2 border-t border-border-color/40 pt-2">
                    <p className="inline-flex items-center gap-2 text-base font-semibold text-zinc-200">
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-violet-600/90">
                        <VerifiedRoundedIcon sx={{ fontSize: 13, color: "#fff" }} />
                      </span>
                      <span>
                        Your plan <span className="text-red-500">*</span>
                      </span>
                    </p>
                    <WhitelabelPackagePicker
                      packages={GUIDE_WL_PACKAGES}
                      selectedPackageId={selectedPackageId}
                      onSelectPackage={setSelectedPackageId}
                      formatPrice={formatPackagePrice}
                      formatPoints={formatPackagePoints}
                    />
                  </div>
                </motion.div>
                <div className="mt-3 flex gap-2">
                  <button type="button" className={`w-1/2 ${BTN_SECONDARY_CLASS}`} onClick={handlePrev}>
                    Back
                  </button>
                  <motion.button
                    id="wl-tour-next-packages"
                    type="button"
                    className={`w-1/2 ${BTN_PRIMARY_CLASS}`}
                    onClick={handleNext}
                    disabled={!selectedPackageId}
                    {...pulseMotion(pulseId, "wl-tour-next-packages")}
                  >
                    Next
                  </motion.button>
                </div>
              </div>
            )}

            {flowStepIndex === 3 && (
              <div className="overflow-visible p-3">
                <div id="wl-tour-review" className="mt-2 space-y-2.5">
                  {(
                    [
                      {
                        key: "brand",
                        label: "Brand name",
                        value: brandName.trim() || "—",
                        icon: <SellRoundedIcon sx={{ fontSize: 16, color: "#fff" }} />,
                        chipClass: "bg-orange-500/90",
                      },
                      {
                        key: "web",
                        label: "Web address",
                        value: webAddressSummary,
                        icon: <PublicRoundedIcon sx={{ fontSize: 16, color: "#fff" }} />,
                        chipClass: "bg-emerald-500/90",
                      },
                      {
                        key: "skin",
                        label: "Skin / Theme",
                        value: `${skinLabel} — ${themeSummary}`,
                        icon: <PaletteRoundedIcon sx={{ fontSize: 16, color: "#fff" }} />,
                        chipClass: "bg-fuchsia-500/90",
                      },
                      {
                        key: "products",
                        label: "Products",
                        value: productsSummary,
                        icon: <Inventory2RoundedIcon sx={{ fontSize: 16, color: "#fff" }} />,
                        chipClass: "bg-sky-500/90",
                      },
                      {
                        key: "payments",
                        label: "Payments",
                        value: paymentsSummary,
                        icon: <CreditCardRoundedIcon sx={{ fontSize: 16, color: "#fff" }} />,
                        chipClass: "bg-amber-500/90",
                      },
                      {
                        key: "plan",
                        label: "Plan",
                        value: planSummary,
                        icon: <VerifiedRoundedIcon sx={{ fontSize: 16, color: "#fff" }} />,
                        chipClass: "bg-violet-600/90",
                      },
                    ] as const
                  ).map((item) => (
                    <div
                      key={item.key}
                      className="flex items-center gap-2.5 rounded-xl border border-[color-mix(in_srgb,var(--border-color)_38%,transparent)] bg-[color-mix(in_srgb,var(--border-color)_14%,#0b1220)] px-2.5 py-2.5 sm:px-3"
                    >
                      <div
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${item.chipClass}`}
                      >
                        {item.icon}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-medium tracking-wide text-[color-mix(in_srgb,var(--border-color)_76%,white)]">
                          {item.label}
                        </p>
                        <p className="text-sm font-medium leading-snug text-zinc-100 sm:text-base">
                          {item.value}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="relative mt-3 w-full">
                  <div className="flex items-start gap-2">
                    <button
                      type="button"
                      className={`w-1/2 shrink-0 ${BTN_SECONDARY_CLASS}`}
                      onClick={handlePrev}
                    >
                      Back
                    </button>
                    <motion.button
                      id="wl-tour-submit"
                      type="button"
                      className={`w-1/2 ${BTN_PRIMARY_CLASS}`}
                      onClick={() => {}}
                      {...pulseMotion(pulseId, "wl-tour-submit")}
                    >
                      Submit
                    </motion.button>
                  </div>
                </div>
              </div>
            )}
            </>
          </div>
        </div>
      </div>
    </div>
  );
});
